import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Eye, Heart, Users, ChefHat, Hammer, Leaf, GraduationCap } from "lucide-react";
import SEO from "../components/SEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0drtr76p_c2c.png";
const BUSINESS_CARD_URL = "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/g7a32qvt_Business%20card%20Front.jpg";

const initiatives = [
  {
    icon: ChefHat,
    title: "C2C Culinary",
    items: [
      "C2C Summer Cooking Camp",
      "C2C Community Cooking",
      "C2C Culinary Buddies - Adopt a Senior",
      "C2C Community Elders Night",
      "C2C Meals on Wheels",
      "Demonstrations & Seminars",
    ],
  },
  {
    icon: Hammer,
    title: "Construction & Trades",
    items: [
      "Basic Construction Skills",
      "Carpentry Training",
      "Tool Safety & Usage",
      "Hands-on Project Work",
      "Job Readiness Skills",
      "Mentorship Programs",
    ],
  },
  {
    icon: Leaf,
    title: "Landscaping",
    items: [
      "Garden Planning & Design",
      "Plant Care & Maintenance",
      "Environmental Stewardship",
      "Community Garden Projects",
      "Sustainable Practices",
      "Team Collaboration",
    ],
  },
  {
    icon: GraduationCap,
    title: "Life Skills",
    items: [
      "Leadership Development",
      "Work Ethics Training",
      "Decision Making Skills",
      "Creative Problem Solving",
      "Financial Literacy",
      "Communication Skills",
    ],
  },
];

const participationOptions = [
  {
    title: "Adults",
    description: "We need your help for C2C execution and expansion.",
    items: ["Organizing", "Chaperoning", "Youth Transportation", "Community Awareness", "Sweat Equity", "Monetary Donations"],
    color: "bg-[#0033A0]",
  },
  {
    title: "Youth",
    description: "All ages welcome with parent/guardian permission.",
    items: ["Learn New Skills", "Meet New People", "Get Community Service Credit", "Earn $10-$15/hour", "Have Fun!", "Build Your Future"],
    color: "bg-[#FFC107]",
    textColor: "text-[#0033A0]",
  },
  {
    title: "Seniors",
    description: "Free programs coming to a neighborhood near you.",
    items: ["Free Meals", "Youth Companionship", "Taste Test Judging", "Share Your Wisdom", "Stay Connected", "Community Events"],
    color: "bg-[#0033A0]",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      <SEO
        title="About Us"
        description="Learn about Confidence 2 Create Inc., founded by Neeko McLemore. C2C bridges generations through culinary, construction, and landscaping programs in Saint Paul, MN."
      />
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/rbkzrbow_Youth%208.jpg" 
            alt="C2C Youth Team" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block bg-[#FFC107] text-[#0033A0] px-4 py-2 rounded-full text-sm font-bold mb-6">
                About Us
              </span>
              <h1 className="text-3xl md:text-6xl font-black text-white mb-6">
                Building Tomorrow's Leaders <span className="text-[#FFC107]">Today</span>
              </h1>
              <p className="text-base md:text-xl text-white/90 leading-relaxed">
                Confidence 2 Create Inc. (C2C) was founded by Neeko McLemore, CEO of McLemore Construction Inc. C2C is about serving and investing where we live and work with initiatives focused on bridging the gap and reviving a lost tradition of "it takes a village."
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <img
                src={LOGO_URL}
                alt="C2C Logo"
                className="w-48 h-48 md:w-80 md:h-80 object-contain"
                data-testid="about-hero-logo"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-10 rounded-2xl border-l-4 border-[#0033A0]"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#0033A0] rounded-xl flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-[#0033A0]">Our Mission</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed">
                C2C is here to educate and close the gap between the young and the older, helping to facilitate bringing unity back into our communities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-10 rounded-2xl border-l-4 border-[#FFC107]"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#FFC107] rounded-xl flex items-center justify-center">
                  <Eye className="w-7 h-7 text-[#0033A0]" />
                </div>
                <h2 className="text-2xl font-bold text-[#0033A0]">Our Vision</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed">
                All initiatives will provide an ongoing forum for local entrepreneurs, leaders and other adults who believe in the importance of "each one, teach one" and are willing to share their knowledge and expertise with our youth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-padding bg-slate-50" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FFC107] font-bold tracking-wide uppercase">Our Founder</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0033A0] mt-4 mb-6">
                Neeko McLemore
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Neeko McLemore, CEO of McLemore Construction Inc., founded Confidence 2 Create Inc. with a vision to resurrect an old tradition of shared experience and teaching youth lifelong skills.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Alongside a network of committed community leaders, Neeko is dedicated to training young people in landscaping, construction, culinary arts, and other critical life disciplines including leadership skills, work ethics, decision-making, and creative problem-solving.
              </p>
              <div className="flex items-center gap-4">
                <Heart className="text-[#FFC107]" size={24} />
                <span className="text-xl font-bold text-[#0033A0] italic">"Each One, Teach One"</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#0033A0] rounded-2xl transform rotate-3" />
                <img
                  src={BUSINESS_CARD_URL}
                  alt="Neeko McLemore Business Card"
                  className="relative rounded-2xl shadow-2xl max-w-md w-full"
                  data-testid="founder-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Initiatives Section */}
      <section className="section-padding bg-white" data-testid="initiatives-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0033A0] mt-4">
              Our Initiatives
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {initiatives.map((initiative, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8 rounded-2xl hover:shadow-lg transition-all card-hover"
                data-testid={`initiative-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#0033A0] rounded-xl flex items-center justify-center mb-6">
                  <initiative.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0033A0] mb-4">{initiative.title}</h3>
                <ul className="space-y-2">
                  {initiative.items.map((item, i) => (
                    <li key={i} className="text-slate-600 flex items-start gap-2">
                      <span className="text-[#FFC107] mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Participation Section */}
      <section className="section-padding bg-slate-50" data-testid="participation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Get Involved</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0033A0] mt-4 mb-4">
              How You Can Participate
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              C2C is open to the public and for everyone. Find out how you can be part of our mission.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {participationOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`${option.color} p-8 rounded-2xl ${option.textColor || "text-white"}`}
                data-testid={`participation-card-${index}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Users className={option.textColor ? "text-[#0033A0]" : "text-[#FFC107]"} size={28} />
                  <h3 className="text-2xl font-bold">{option.title}</h3>
                </div>
                <p className={`mb-6 ${option.textColor ? "text-[#0033A0]/80" : "text-white/90"}`}>
                  {option.description}
                </p>
                <ul className="space-y-2">
                  {option.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className={option.textColor ? "text-[#0033A0]" : "text-[#FFC107]"}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0033A0]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Join Our Community
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Together, we can build a stronger community where everybody eats and everybody succeeds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/donations"
                data-testid="about-cta-donate"
                className="btn-secondary"
              >
                <Heart className="inline mr-2" size={20} />
                Support Our Mission
              </Link>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="bg-white text-[#0033A0] hover:bg-slate-100 px-8 py-3 rounded-full font-bold transition-all"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
